import { generatePath } from "react-router";
import { type CustomCellRendererProps } from "ag-grid-react";
import { differenceInCalendarDays } from "date-fns";
import { MoreVerticalIcon } from "vega-icons";

import { type ApiGatewayWorkbenchApiKey } from "@vega-api/generated/types/apiGatewayWorkbenchApiKey";
import { CopyButton } from "@vega-core/components/Buttons/CopyButton/CopyButton";
import { LinkTextButton } from "@vega-core/components/Buttons/LinkTextButton";
import { type DataGridColDef } from "@vega-core/components/DataGrid/types";
import { useColumnToggle } from "@vega-core/components/DataGrid/useColumnToggle";
import { Button } from "@vega-core/components/shadcn/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@vega-core/components/shadcn/dropdown-menu";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import {
  DateFormatString,
  formatShortSlashDate,
} from "@vega-core/utils/formatDate";
import { VegaModulesPaths } from "@vega-modules/paths";

import { KeyStatusTag } from "./ApiKeysTable";
import { ApiKeysTableHeader } from "./ApiKeysTableHeader";
import { deriveKeyStatus, KeyStatus } from "./deriveKeyStatus";

const EXPIRY_HIGHLIGHT_DAYS = 30;

type Row = ApiGatewayWorkbenchApiKey;

type CellProps = CustomCellRendererProps<Row>;

/**
 * Listing order is owned by the server (sortBy/sortDirection params), so
 * AG Grid must not re-order the delivered page client-side. A constant
 * comparator keeps AG Grid's stable sort a no-op while the header sort UI
 * still cycles and emits sort events for the server round-trip.
 */
const SERVER_OWNED_ORDER = () => 0;

type UseApiKeysColumnsProps = {
  /** Stable "now" anchor so status derivation is consistent within a paint. */
  now: Date;
  onRotate: (key: ApiGatewayWorkbenchApiKey) => void;
  onDeactivate: (key: ApiGatewayWorkbenchApiKey) => void;
  onDeactivateAll: (key: ApiGatewayWorkbenchApiKey) => void;
};

export const useApiKeysColumns = ({
  now,
  onRotate,
  onDeactivate,
  onDeactivateAll,
}: UseApiKeysColumnsProps) => {
  const withHeader = (tooltip: string) => ({
    headerComponent: ApiKeysTableHeader,
    headerComponentParams: { tooltip },
  });

  const renderActionsCell = ({ data }: CellProps) => {
    if (!data) return null;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            data-testid={`key-actions-${data.userId}-${data.keyName}`}
            size="sm"
            variant="ghost"
          >
            <MoreVerticalIcon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onRotate(data)}>
            {m.settings_reportingApiKeys_action_rotate()}
          </DropdownMenuItem>
          {deriveKeyStatus(data, now) !== KeyStatus.Deactivated ? (
            <DropdownMenuItem onClick={() => onDeactivate(data)}>
              {m.settings_reportingApiKeys_action_deactivate()}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onClick={() => onDeactivateAll(data)}>
            {m.settings_reportingApiKeys_action_deactivateAll()}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const rawColumns: DataGridColDef<Row>[] = [
    {
      colId: "keyName",
      field: "keyName",
      headerName: m.settings_reportingApiKeys_column_keyName(),
      comparator: SERVER_OWNED_ORDER,
      ...withHeader(m.settings_reportingApiKeys_column_keyName_tooltip()),
      cellRenderer: ({ data }: CellProps) => (
        <span className="body-base-medium">{data?.keyName}</span>
      ),
    },
    {
      colId: "userName",
      field: "userName",
      headerName: m.settings_reportingApiKeys_column_user(),
      comparator: SERVER_OWNED_ORDER,
      toggleable: true,
      ...withHeader(m.settings_reportingApiKeys_column_user_tooltip()),
      cellRenderer: ({ data }: CellProps) => {
        if (!data) return null;
        const { userId, userName } = data;

        if (userName === null || userName === undefined) {
          return (
            <span className="flex items-center gap-1">
              <span className="text-text/secondary">
                {m.settings_reportingApiKeys_unknownUser()}
              </span>
              <span className="body-small-regular text-text/secondary">
                {`${userId.slice(0, 8)}…`}
              </span>
              <CopyButton text={userId} />
            </span>
          );
        }

        return (
          <LinkTextButton
            route={generatePath(VegaModulesPaths.InvestorDashboard, {
              investorId: userId,
            })}
            text={userName}
          />
        );
      },
    },
    {
      colId: "organizationName",
      field: "organizationName",
      headerName: m.settings_reportingApiKeys_column_organization(),
      comparator: SERVER_OWNED_ORDER,
      toggleable: true,
      ...withHeader(m.settings_reportingApiKeys_column_organization_tooltip()),
      cellRenderer: ({ data }: CellProps) => data?.organizationName ?? "—",
    },
    {
      colId: "status",
      headerName: m.settings_reportingApiKeys_column_status(),
      comparator: SERVER_OWNED_ORDER,
      toggleable: true,
      ...withHeader(m.settings_reportingApiKeys_column_status_tooltip()),
      cellRenderer: ({ data }: CellProps) =>
        data ? <KeyStatusTag status={deriveKeyStatus(data, now)} /> : null,
    },
    {
      colId: "createdAt",
      field: "createdAt",
      headerName: m.settings_reportingApiKeys_column_created(),
      comparator: SERVER_OWNED_ORDER,
      toggleable: true,
      ...withHeader(m.settings_reportingApiKeys_column_created_tooltip()),
      cellRenderer: ({ data }: CellProps) =>
        data
          ? formatShortSlashDate(data.createdAt, DateFormatString.UK_DATE_SLASH)
          : null,
    },
    {
      colId: "expiresAt",
      field: "expiresAt",
      headerName: m.settings_reportingApiKeys_column_expires(),
      comparator: SERVER_OWNED_ORDER,
      toggleable: true,
      ...withHeader(m.settings_reportingApiKeys_column_expires_tooltip()),
      cellRenderer: ({ data }: CellProps) => {
        if (!data) return null;
        const expiresAt = new Date(data.expiresAt);
        const expiresSoon =
          expiresAt > now &&
          differenceInCalendarDays(expiresAt, now) <= EXPIRY_HIGHLIGHT_DAYS;

        return (
          <span
            className={cnTwMerge({
              "text-text/warning body-base-medium": expiresSoon,
            })}
          >
            {formatShortSlashDate(
              data.expiresAt,
              DateFormatString.UK_DATE_SLASH,
            )}
          </span>
        );
      },
    },
    {
      colId: "lastUsedAt",
      field: "lastUsedAt",
      headerName: m.settings_reportingApiKeys_column_lastUsed(),
      comparator: SERVER_OWNED_ORDER,
      toggleable: true,
      ...withHeader(m.settings_reportingApiKeys_column_lastUsed_tooltip()),
      cellRenderer: ({ data }: CellProps) => {
        if (!data) return null;

        if (data.lastUsedAt === null || data.lastUsedAt === undefined) {
          return m.settings_reportingApiKeys_lastUsed_never();
        }

        return formatShortSlashDate(
          data.lastUsedAt,
          DateFormatString.UK_DATE_SLASH,
        );
      },
    },
    {
      colId: "actions",
      headerName: "",
      sortable: false,
      resizable: false,
      flex: 0,
      width: 64,
      minWidth: 64,
      pinned: "right",
      // Flex layout so the cell never paints a text-overflow ellipsis next
      // to the kebab when the button brushes the padded content box.
      cellClass: "flex items-center justify-center",
      cellRenderer: renderActionsCell,
    },
  ];

  // No storage passed: column visibility is deliberately ephemeral (per visit).
  return useColumnToggle(rawColumns);
};
